/*
 * Copyright (c) 2023. Bluemate B.V.
 */
import './style/createRatingExport.scss';

import {
    ChangeFunction,
    Checkbox,
    CloseButton,
    DvrdSelect,
    Loader,
    voidFunction,
    WithBackground
} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import React, {MouseEventHandler, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Rating} from '../../../util/types';

import {BluemateButton, DvrdDatePicker} from '../../controls';

import BolRating from '../../bolRating/bolRating';

import {AppContext} from '../../misc/appContext';

import Account from '../../../models/accountModel';

interface Props {
    onClose: MouseEventHandler;
    onChangeFromDate: ChangeFunction<IDate>;
    onChangeToDate: ChangeFunction<IDate>;
    onToggleAllRatings: ChangeFunction<boolean>;
    onToggleRating: (rating: Rating) => MouseEventHandler;
    onStartExport: MouseEventHandler;
    onSelectAccounts: ChangeFunction<Array<string>>;
    fromDate: IDate | null;
    toDate: IDate | null;
    onlyRatings: Set<Rating>;
    ean?: string;
    loading: boolean;
    active: boolean;
    selectedAccounts: Set<string>;
}

export default function CreateRatingExport(props: Props) {
    const {t} = useTranslation();
    const {accounts} = useContext(AppContext).accountContext;
    const {
        ean,
        fromDate,
        onChangeFromDate,
        onChangeToDate,
        toDate,
        onToggleRating,
        onlyRatings,
        onStartExport,
        onClose,
        active,
        loading,
        onToggleAllRatings,
        onSelectAccounts,
        selectedAccounts
    } = props;
    const headerTitle = useMemo(() => {
        if (ean) return `${t('reviewAnalytics.export.exportReviewsFor', 'Reviews exporteren voor')} ${ean}`;
        return t('reviewAnalytics.export.exportReviews', 'Reviews exporteren');
    }, [ean, t]);
    const accountItems = useMemo(() => {
        return accounts.map((account: Account) => ({value: account.id, label: account.labelWithSellerID}));
    }, [accounts]);

    function renderBody() {
        const dateFormat = 'DD-MM-YYYY';
        const allSelected = ![1, 2, 3, 4, 5].find((rating: Rating) => !onlyRatings.has(rating));
        const allLabel = allSelected
            ? t('reviewAnalytics.export.deselect', 'deselecteren')
            : t('reviewAnalytics.export.select', 'selecteren');
        return (
            <div className='body-container'>
                <Loader active={loading} />
                {renderAccounts()}
                <div className='dates'>
                    <DvrdDatePicker
                        onChange={onChangeFromDate}
                        value={fromDate}
                        label={t('reviewAnalytics.export.fromDate', 'Vanaf datum')}
                        dateFormat={dateFormat}
                        placeholder={t('reviewAnalytics.export.optional', 'Optioneel')}
                        closeOnChange
                    />
                    <DvrdDatePicker
                        onChange={onChangeToDate}
                        value={toDate}
                        label={t('reviewAnalytics.export.toDate', 'T/m datum')}
                        dateFormat={dateFormat}
                        placeholder={t('reviewAnalytics.export.optional', 'Optioneel')}
                        closeOnChange
                    />
                </div>
                <div className='rating-selection'>
                    <strong className='rating-title grid-col-2'>
                        {t('reviewAnalytics.export.filterByScore', 'Filteren op score')}
                    </strong>
                    <Checkbox
                        checked={allSelected}
                        onCheck={onToggleAllRatings}
                        label={`${t('reviewAnalytics.export.all', 'Alles')} ${allLabel}`}
                        className='grid-col-2'
                    />
                    <div className='col'>
                        {renderRatingSelect(1)}
                        {renderRatingSelect(2)}
                        {renderRatingSelect(3)}
                    </div>
                    <div className='col'>
                        {renderRatingSelect(4)}
                        {renderRatingSelect(5)}
                    </div>
                </div>
            </div>
        );
    }

    function renderAccounts() {
        return (
            <div className='accounts-container'>
                <DvrdSelect<any>
                    items={accountItems}
                    value={Array.from(selectedAccounts)}
                    onChange={onSelectAccounts}
                    label={t('reviewAnalytics.export.exportForAccounts', 'Reviews exporteren voor accounts')}
                    multi
                />
            </div>
        );
    }

    function renderRatingSelect(rating: Rating) {
        return (
            <div className='rating-select' onClick={onToggleRating(rating)}>
                <Checkbox checked={onlyRatings.has(rating)} onCheck={voidFunction} />
                <BolRating value={rating} staticStars />
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='create-rating-export default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{headerTitle}</label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BluemateButton
                        onClick={onClose}
                        label={t('reviewAnalytics.export.cancel', 'Annuleren')}
                        disabled={loading}
                        secondary
                    />
                    <BluemateButton
                        onClick={onStartExport}
                        label={t('reviewAnalytics.export.export', 'Exporteren')}
                        disabled={loading}
                    />
                </div>
            </div>
        </WithBackground>
    );
}
